"use client";
/**
 * A persisted nutrition block, drawn by the nutrition family.
 *
 * The card owns no composition of its own. It sits on the shared attach plate —
 * the same material every other block is drawn on — and mounts
 * `CnutritioncardSet` inside it, so everything below the plate is the family's
 * own geometry.
 *
 * THE SERVING IS STATED ON EVERY FIGURE, and that is why the reading is PINNED
 * rather than read off the tweak registry. One block can hold a food measured
 * `per 100 g` beside one measured `per 4 oz`, and a persisted block has no
 * running frame line above it to say which — so each figure carries its own
 * basis.
 *
 * A block whose items did not read as this pillar's shape draws the lossless
 * render instead. The shared reader has already degraded such a block whole
 * (D28), so the fallback is a fact about the block rather than a failure here.
 */
import type { ReactElement } from "react";
import { IconSalad } from "@tabler/icons-react";
import type { ParsedAttachResults } from "@cabal/shared/domain/attached-result/index.js";
import {
  CresultcardLayout,
  type CresultcardSetEmpty,
} from "@/components/ui/Cresultcard";
import {
  CnutritioncardServingScope,
  CnutritioncardSet,
} from "@/components/ui/Cnutritioncard";
import { CATTACHEDRESULT_PLATE_STYLE } from "./cattachedresult.plate";
import { cattachedresultNutrition } from "./cattachedresult.nutrition.model";
import { CattachedresultParsedItems } from "./CattachedresultParsedItems";

const SET_LABEL = "Foods and recipes";
const BLOCK_LABEL = "Attached nutrition";
const EMPTY: CresultcardSetEmpty = {
  headline: "Nothing in this block",
  reason: "The attached search stored no food or recipe row.",
};

export interface CattachedresultNutritionProps {
  readonly block: ParsedAttachResults;
}

export function CattachedresultNutrition({
  block,
}: CattachedresultNutritionProps): ReactElement {
  const items = cattachedresultNutrition({ block });

  if (items.length === 0) {
    return <CattachedresultParsedItems block={block} />;
  }

  return (
    <section
      aria-label={block.about ?? BLOCK_LABEL}
      style={CATTACHEDRESULT_PLATE_STYLE}
    >
      <CnutritioncardSet
        context={block.about ?? undefined}
        drillContext={block.about ?? BLOCK_LABEL}
        empty={EMPTY}
        icon={IconSalad}
        items={items}
        label={SET_LABEL}
        layout={CresultcardLayout.Cards}
        provenance={block.capturedAtLabel ?? undefined}
        servingScope={CnutritioncardServingScope.Stated}
      />
    </section>
  );
}
